/**
 * Community Membership Query Hooks
 * React Query hooks for community membership-related data fetching
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { NotificationsResponse } from "@/features/notifications/services/notification.service"
import * as communityService from "../services/community.service"

/**
 * Hook to check community membership status
 * @param communityId - The community ID to check membership for
 * @param skip - If true, skip fetching (useful when status is already known)
 */
export function useCommunityMembership(communityId: string, skip = false) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["community-membership", communityId],
    queryFn: () => communityService.getCommunityMembership(communityId),
    enabled: !!communityId && !skip,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  return {
    isJoined: data?.isMember || false,
    isPending: data?.isPending || false,
    memberSince: data?.memberSince,
    isLoading,
    error,
  }
}

/**
 * Hook for join/leave mutations
 */
export function useCommunityJoinActions() {
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore(state => state.user?.id)

  const joinMutation = useMutation({
    mutationFn: (communityId: string) => communityService.joinCommunity(communityId),
    onSuccess: (data, communityId) => {
      queryClient.invalidateQueries({ queryKey: ["community-membership", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community-members"] })
      queryClient.invalidateQueries({ queryKey: ["userCommunities"] })
      queryClient.invalidateQueries({ queryKey: ["community-statistics", communityId] })
      queryClient.invalidateQueries({ queryKey: ["connect-featured-community-rituals"] })
      queryClient.invalidateQueries({ queryKey: ["connect-communities"] })
      if (currentUserId) {
        queryClient.invalidateQueries({ queryKey: ["profileStatistics", currentUserId] })
      }
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to join community")
    },
  })

  const leaveMutation = useMutation({
    mutationFn: (communityId: string) => communityService.leaveCommunity(communityId),
    onSuccess: (data, communityId) => {
      queryClient.invalidateQueries({ queryKey: ["community-membership", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community-members"] })
      queryClient.invalidateQueries({ queryKey: ["userCommunities"] })
      queryClient.invalidateQueries({ queryKey: ["community-statistics", communityId] })
      queryClient.invalidateQueries({ queryKey: ["connect-featured-community-rituals"] })
      queryClient.invalidateQueries({ queryKey: ["connect-communities"] })
      if (currentUserId) {
        queryClient.invalidateQueries({ queryKey: ["profileStatistics", currentUserId] })
      }
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to leave community")
    },
  })

  const cancelRequestMutation = useMutation({
    mutationFn: (communityId: string) => communityService.cancelJoinRequest(communityId),
    onSuccess: (data, communityId) => {
      queryClient.invalidateQueries({ queryKey: ["community-membership", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community", communityId] })
      queryClient.invalidateQueries({ queryKey: ["community-members"] })
      queryClient.invalidateQueries({ queryKey: ["connect-featured-community-rituals"] })
      queryClient.invalidateQueries({ queryKey: ["connect-communities"] })
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to cancel request")
    },
  })

  return {
    join: joinMutation.mutate,
    leave: leaveMutation.mutate,
    cancelRequest: cancelRequestMutation.mutate,
    isJoining: joinMutation.isPending,
    isLeaving: leaveMutation.isPending,
    isCancelling: cancelRequestMutation.isPending,
  }
}

/**
 * Hook to get join requests for a community (admin only)
 * @param communityId - The community ID
 * @param options - Query options (page, limit)
 * @param enabled - Whether the query should be enabled
 */
export function useJoinRequests(
  communityId: string,
  options?: { page?: number; limit?: number },
  enabled = true,
) {
  const { page = 1, limit = 20 } = options || {}

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["join-requests", communityId, page, limit],
    queryFn: () => communityService.getJoinRequests(communityId, { page, limit }),
    enabled: !!communityId && enabled,
    refetchOnWindowFocus: false,
  })

  return {
    requests: data?.requests || [],
    total: data?.total || 0,
    totalPages: data?.totalPages || 1,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook for join request action mutations (admin only)
 */
export function useJoinRequestActions() {
  const queryClient = useQueryClient()

  const acceptMutation = useMutation({
    mutationFn: ({ communityId, requestId }: { communityId: string; requestId: string }) =>
      communityService.acceptJoinRequest(communityId, requestId),
    onMutate: variables => {
      // Optimistically update notification cache to hide buttons immediately
      queryClient.setQueryData(["notifications"], (old: unknown) => {
        const currentData = old as NotificationsResponse | undefined
        if (!currentData?.notifications) return old

        return {
          ...currentData,
          notifications: currentData.notifications.map(n =>
            n.entityId === variables.requestId && n.action === "join_request_received"
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    actionTakenBy: "current-user", // Temporary, will be updated by WebSocket
                    actionTakenByName: "You", // Temporary, will be updated by WebSocket
                    action: "accepted",
                  },
                }
              : n,
          ),
        }
      })
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["join-requests", variables.communityId] })
      queryClient.invalidateQueries({ queryKey: ["community-membership"] })
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      // Don't invalidate notifications - socket event will handle the update
      // This prevents race condition where refetch overwrites socket-updated data
      toast.error(error.response?.data?.message || "Failed to accept request")
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({
      communityId,
      requestId,
      reason,
    }: {
      communityId: string
      requestId: string
      reason?: string
    }) => communityService.rejectJoinRequest(communityId, requestId, reason),
    onMutate: variables => {
      // Optimistically update notification cache to hide buttons immediately
      queryClient.setQueryData(["notifications"], (old: unknown) => {
        const currentData = old as NotificationsResponse | undefined
        if (!currentData?.notifications) return old

        return {
          ...currentData,
          notifications: currentData.notifications.map(n =>
            n.entityId === variables.requestId && n.action === "join_request_received"
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    actionTakenBy: "current-user", // Temporary, will be updated by WebSocket
                    actionTakenByName: "You", // Temporary, will be updated by WebSocket
                    action: "rejected",
                  },
                }
              : n,
          ),
        }
      })
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["join-requests", variables.communityId] })
      queryClient.invalidateQueries({ queryKey: ["community-membership"] })
      toast.info(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      // Don't invalidate notifications - socket event will handle the update
      // This prevents race condition where refetch overwrites socket-updated data
      toast.error(error.response?.data?.message || "Failed to reject request")
    },
  })

  return {
    acceptRequest: acceptMutation.mutate,
    rejectRequest: rejectMutation.mutate,
    isAccepting: acceptMutation.isPending,
    isRejecting: rejectMutation.isPending,
  }
}
